import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  Cloud,
} from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { authAPI } from "../services/api";
import { InlineLoadSkeleton } from "./common/InlineLoadSkeleton";

const LoginForm = () => {
  const {
    login,
    verifyOTP,
    verifyMfa,
    loginWithGoogle,
    setAuthFromLoginResult,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Force light theme on this component (OTP step is part of login)
  useEffect(() => {
    // Store original theme state
    const hadDarkClass = document.documentElement.classList.contains("dark");
    const savedTheme = localStorage.getItem("theme") || "LIGHT";

    // Force light theme
    document.documentElement.classList.remove("dark");

    // Restore original theme on unmount
    return () => {
      // Check if we should restore dark theme based on saved preference
      if (
        savedTheme === "DARK" ||
        (savedTheme === "SYSTEM" &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      ) {
        document.documentElement.classList.add("dark");
      } else if (hadDarkClass && savedTheme !== "LIGHT") {
        // If it was dark before and not explicitly LIGHT, restore it
        document.documentElement.classList.add("dark");
      }
    };
  }, []);
  const [step, setStep] = useState(1); // 1: credentials, 2: OTP
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    otp: "",
    mfaCode: "",
  });
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [emailForOtp, setEmailForOtp] = useState("");
  const [mfaTempToken, setMfaTempToken] = useState("");
  const [skippedOtpForMfa, setSkippedOtpForMfa] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const redirectAfterLogin = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    if (!redirect) return null;
    if (!redirect.startsWith("/")) return null;
    return redirect;
  }, [location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(""); // Clear error when user types
    setSuccess(""); // Clear success message when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (step === 1) {
        // Step 1: Send credentials — may return token, requires_mfa, mfa_setup_required, or otp_sent
        const response = await login(formData.email, formData.password);
        const data = response?.data ?? response;

        if (data?.auth_token && data?.user) {
          if (typeof setAuthFromLoginResult === "function") {
            setAuthFromLoginResult(data.auth_token, data.user, data.expires_at);
          }
          const requiresPasswordChange =
            data.requires_password_change ||
            data.redirect_path === "/change-password";
          if (requiresPasswordChange) {
            navigate("/change-password");
            return;
          }
          const role = data.user?.role_name || data.user?.role;
          const norm = (r) =>
            r ? r.toLowerCase().replace(/[_\s-]/g, "-") : null;
          if (redirectAfterLogin) {
            navigate(redirectAfterLogin, { replace: true });
            return;
          }
          if (norm(role) === "chief-admin") navigate("/clients", { replace: true });
          else navigate("/projects", { replace: true });
          return;
        }
        if (data?.requires_mfa && data?.mfa_temp_token) {
          setEmailForOtp(formData.email);
          setMfaTempToken(data.mfa_temp_token);
          setSkippedOtpForMfa(true);
          setSuccess(
            data?.message || "Enter the code from your authenticator app.",
          );
          setStep(3);
          setFormData((prev) => ({ ...prev, mfaCode: "" }));
          return;
        }
        if (data?.mfa_setup_required) {
          setError(
            data?.message ||
              "MFA setup is required. Please contact your administrator to receive the MFA setup link.",
          );
          return;
        }

        // Default: OTP sent (client MFA off)
        setEmailForOtp(formData.email);
        setMaskedEmail(data?.email || "your email");
        setSuccess("OTP sent successfully! Please check your email.");
        setStep(2);
        setResendDisabled(true);
        setResendCountdown(60);
        const countdownInterval = setInterval(() => {
          setResendCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              setResendDisabled(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (step === 2) {
        // Step 2: Verify OTP
        const response = await verifyOTP(emailForOtp, formData.otp);
        if (response?.requires_mfa && response?.mfa_temp_token) {
          setMfaTempToken(response.mfa_temp_token);
          setSuccess(
            response?.message || "Enter the code from your authenticator app.",
          );
          setStep(3);
          setFormData((prev) => ({ ...prev, otp: "", mfaCode: "" }));
          return;
        }
        const userData = response?.user || response?.data?.user;
        const requiresPasswordChange =
          response?.data?.requires_password_change ||
          response?.requires_password_change ||
          response?.data?.redirect_path === "/change-password";
        if (requiresPasswordChange) {
          navigate("/change-password");
          return;
        }
        // Handle redirect based on user role in UI (not from backend)
        // Use user data directly from response (available immediately)
        // Check role_name from roles table first, then fallback to role for backward compatibility
        let userRole = null;

        // Priority: 1) userData from response (most reliable), 2) stored user from localStorage
        if (userData) {
          userRole = userData.role_name || userData.role;
        }

        if (!userRole) {
          // Fallback: try to get from stored user (should be set by AuthContext immediately)
          try {
            const storedUser = JSON.parse(
              localStorage.getItem("user") || "null",
            );
            if (storedUser) {
              userRole = storedUser.role_name || storedUser.role;
            }
          } catch (e) {
            console.error("Error parsing stored user:", e);
          }
        }

        // Normalize role for comparison (handle variations like 'chief-admin', 'chief_admin', 'Chief Admin')
        const normalizeRole = (role) => {
          if (!role) return null;
          return role.toLowerCase().replace(/[_\s-]/g, "-");
        };

        const normalizedRole = normalizeRole(userRole);

        // Perform redirect based on role
        if (redirectAfterLogin) {
          navigate(redirectAfterLogin, { replace: true });
        } else if (normalizedRole === "chief-admin") {
          navigate("/clients", { replace: true });
        } else if (userRole) {
          navigate("/projects", { replace: true });
        } else {
          console.error(
            "No user role found! Response:",
            response,
            "UserData:",
            userData,
          );
          navigate("/projects", { replace: true });
        }
      } else if (step === 3) {
        // Step 3: Verify MFA code (when client has MFA enabled)
        const response = await verifyMfa(mfaTempToken, formData.mfaCode);
        const userData = response?.user || response?.data?.user;
        const requiresPasswordChange =
          response?.data?.requires_password_change ||
          response?.requires_password_change ||
          response?.data?.redirect_path === "/change-password";
        if (requiresPasswordChange) {
          navigate("/change-password");
          return;
        }
        let userRole = userData?.role_name || userData?.role;
        const normalizeRole = (role) =>
          role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
        const normalizedRole = normalizeRole(userRole);
        if (redirectAfterLogin) {
          navigate(redirectAfterLogin, { replace: true });
        } else if (normalizedRole === "chief-admin") {
          navigate("/clients", { replace: true });
        } else {
          navigate("/projects", { replace: true });
        }
      }
    } catch (err) {
      // Handle specific error messages from the API
      let errorMessage = "An error occurred. Please try again.";

      // Check for specific error codes first
      if (
        err.response?.data?.error === "CLIENT_INACTIVE" ||
        err.response?.data?.error_code === "CLIENT_INACTIVE"
      ) {
        errorMessage =
          err.response.data.message ||
          "Client is Inactive. Contact sales to get activated.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 401) {
        if (step === 1) {
          errorMessage =
            "Invalid email or password. Please check your credentials and try again.";
        } else if (step === 3) {
          errorMessage =
            err.response?.data?.message ||
            "Invalid authenticator code or session expired. Please log in again.";
        } else {
          errorMessage =
            "Invalid OTP entered. Please check the code and try again.";
        }
      } else if (err.response?.status === 400) {
        errorMessage =
          err.response.data?.message ||
          "Invalid request. Please check your input and try again.";
      } else if (err.response?.status === 403) {
        // Check if it's CLIENT_INACTIVE or account deactivated
        if (
          err.response?.data?.error === "CLIENT_INACTIVE" ||
          err.response?.data?.error_code === "CLIENT_INACTIVE"
        ) {
          errorMessage =
            err.response.data.message ||
            "Client is Inactive. Contact sales to get activated.";
        } else {
          errorMessage =
            "Account is deactivated. Please contact administrator.";
        }
      } else if (err.response?.status === 429) {
        errorMessage =
          "Too many attempts. Please wait a moment before trying again.";
      } else if (err.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (step === 3) {
      if (skippedOtpForMfa) {
        setStep(1);
        setMfaTempToken("");
        setSkippedOtpForMfa(false);
        setFormData((prev) => ({ ...prev, mfaCode: "" }));
      } else {
        setStep(2);
        setMfaTempToken("");
        setFormData((prev) => ({ ...prev, mfaCode: "" }));
        setSuccess("Enter the OTP sent to your email.");
      }
    } else {
      setStep(1);
      setFormData((prev) => ({ ...prev, otp: "" }));
      setEmailForOtp("");
      setMaskedEmail("");
      setResendDisabled(false);
      setResendCountdown(0);
    }
    setError("");
  };

  const handleResendOTP = async () => {
    if (resendDisabled) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await login(emailForOtp, formData.password);
      setSuccess("New OTP sent successfully! Please check your email.");

      // Start resend countdown again
      setResendDisabled(true);
      setResendCountdown(60);
      const countdownInterval = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      // Handle specific error messages from the API
      let errorMessage = "Failed to resend OTP. Please try again.";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 401) {
        errorMessage =
          "Invalid credentials provided. Please check your email and password.";
      } else if (err.response?.status === 429) {
        errorMessage =
          "Too many attempts. Please wait a moment before trying again.";
      } else if (err.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError("");

    try {
      // Get OAuth URL from backend
      const response = await authAPI.initiateGoogleOAuth();

      if (response.success && response.auth_url) {
        // Open OAuth popup window
        const width = 500;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(
          response.auth_url,
          "Google Sign In",
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`,
        );

        // Listen for OAuth callback message
        const handleMessage = async (event) => {
          // Verify origin for security (allow localhost for development)
          const userServiceUrl =
            process.env.REACT_APP_USER_SERVICE_URL || "http://localhost:3001";
          const allowedOrigins = [
            userServiceUrl,
            window.location.origin,
            "http://localhost:3001",
            "http://localhost:4000",
            "http://127.0.0.1:3001",
            "http://127.0.0.1:4000",
          ];

          // In development, allow localhost origins
          const isDevelopment =
            process.env.NODE_ENV !== "production" ||
            event.origin.includes("localhost") ||
            event.origin.includes("127.0.0.1");

          if (!isDevelopment && !allowedOrigins.includes(event.origin)) {
            console.warn(
              "Rejected message from unauthorized origin:",
              event.origin,
            );
            return;
          }

          if (event.data && event.data.type === "google_sso_success") {
            window.removeEventListener("message", handleMessage);
            popup?.close();

            const { token, user: userData } = event.data;

            if (token && userData) {
              // Login user with Google SSO (this will refresh user data from API)
              await loginWithGoogle(token, userData);

              // Wait a bit for user data to be refreshed
              await new Promise((resolve) => setTimeout(resolve, 500));

              // Redirect based on role (check role_name from roles table first)
              const userRole = userData.role_name || userData.role;

              // Normalize role for comparison (handle variations like 'chief-admin', 'chief_admin', 'Chief Admin')
              const normalizeRole = (role) => {
                if (!role) return null;
                return role.toLowerCase().replace(/[_\s-]/g, "-");
              };

              const normalizedRole = normalizeRole(userRole);

              if (redirectAfterLogin) {
                navigate(redirectAfterLogin, { replace: true });
              } else if (normalizedRole === "chief-admin") {
                navigate("/clients", { replace: true });
              } else {
                navigate("/projects", { replace: true });
              }
            } else {
              setError("Failed to complete Google sign in. Please try again.");
            }
            setGoogleLoading(false);
          } else if (event.data && event.data.type === "google_sso_error") {
            window.removeEventListener("message", handleMessage);
            popup?.close();
            setError(
              event.data.message || "Google sign in failed. Please try again.",
            );
            setGoogleLoading(false);
          }
        };

        window.addEventListener("message", handleMessage);

        // Check if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          setError(
            "Popup blocked. Please allow popups for this site and try again.",
          );
          setGoogleLoading(false);
          return;
        }

        // Check if popup is closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener("message", handleMessage);
            setGoogleLoading(false);
          }
        }, 1000);
      } else {
        setError("Failed to initiate Google sign in. Please try again.");
        setGoogleLoading(false);
      }
    } catch (err) {
      console.error("Google login error:", err);
      let errorMessage = "Failed to sign in with Google. Please try again.";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 429) {
        errorMessage =
          "Too many attempts. Please wait a moment before trying again.";
      } else if (err.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      }

      setError(errorMessage);
      setGoogleLoading(false);
    }
  };

  return (
    <div className="w-full max-w-[420px] mx-auto mt-5 mb-5">
      {/* Branding - Logo + tagline + slogan per design */}
      <div className="">
        <div className="flex items-center gap-2.5 mb-3 justify-center">
          <span className="font-bold text-[#504749FF] text-4xl">
            Welcome Back
          </span>
        </div>
      </div>

      {/* Header */}
      <div className="mb-8 flex flex-col items-center">
        <h1 className="text-2xl font-bold text-[#2F292BFF] mb-2 mt-4 justify-center">
          {step === 1
            ? "Login to your Account"
            : step === 3
              ? "Authenticator Code"
              : "Verify Your Account"}
        </h1>
        <p className="text-[#E4E0E1FF] text-base justify-center font-[Inter] font-[700]">
          {step === 1
            ? "Streamline your QA Process"
            : step === 3
              ? "Enter the code from your authenticator app"
              : `Enter the 6-digit code sent to ${maskedEmail}`}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-600 font-medium">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-600 font-medium">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
        {step === 1 ? (
          // Step 1: Email and Password
          <>
            <div>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Work Email"
                autoComplete="email"
                leftIcon={<Mail className="w-5 h-5 text-[#9ca3af]" />}
                className="bg-[#f9fafb] border-[#e5e7eb] rounded-lg h-12"
                required
              />
            </div>

            <div>
              <Input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Password"
                autoComplete="current-password"
                leftIcon={<Lock className="w-5 h-5 text-[#9ca3af]" />}
                rightIcon={
                  showPassword ? (
                    <EyeOff className="w-5 h-5 text-[#9ca3af]" />
                  ) : (
                    <Eye className="w-5 h-5 text-[#9ca3af]" />
                  )
                }
                onRightIconClick={() => setShowPassword(!showPassword)}
                className="bg-[#f9fafb] border-[#e5e7eb] rounded-lg h-12"
                required
              />
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <Checkbox
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="bg-white border-[#655B5EFF] data-[state=checked]:bg-[#1D191AFF] data-[state=checked]:border-[#1D191AFF] [&>span>svg]:text-black"
                />
                <span className="text-sm text-[#374151]">Remember Me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-[#125D95FF] hover:text-[#125D95FF] font-medium underline"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading || !formData.email || !formData.password}
              className="w-full h-12 bg-[#1D191AFF] hover:bg-[#1D191AFF] text-white rounded-lg font-medium text-base"
            >
              {loading ? (
                <>
                  <InlineLoadSkeleton className="h-4 w-4 mr-2 rounded-full bg-white/30" />
                  <span>Logging In</span>
                </>
              ) : (
                <span>Login</span>
              )}
            </Button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-dashed border-[#1D191AFF]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-1 bg-[#B1ABADFF] text-[#1D191AFF] font-normal">
                  OR
                </span>
              </div>
            </div>

            {/* Social Sign In Buttons */}
            <div className="space-y-3">
              <Button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading || loading}
                variant="outline"
                style={{ opacity: googleLoading || loading ? 0.4 : 1 }}
                className="w-full h-12 bg-[#DB4437FF] hover:bg-[#DB4437FF] text-white border-0 rounded-lg font-medium"
              >
                {googleLoading ? (
                  <>
                    <InlineLoadSkeleton className="h-5 w-5 mr-2 rounded-full bg-white/30" />
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5 mr-2 text-white"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span>Continue with Google</span>
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={microsoftLoading || loading}
                style={{ opacity: microsoftLoading || loading ? 0.4 : 1 }}
                className="w-full h-12 bg-[#4267B2FF] hover:bg-[#4267B2FF] text-white border-0 opacity-70 cursor-not-allowed rounded-lg font-medium"
                title="Microsoft sign in coming soon"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  viewBox="0 0 23 23"
                  fill="currentColor"
                >
                  <path d="M11 11h10v10H11zM11 1h10v10H11zM1 1h10v10H1zM1 12h10v10H1z" />
                </svg>
                <span>Continue with Microsoft</span>
              </Button>
            </div>
          </>
        ) : step === 2 ? (
          // Step 2: OTP Verification
          <>
            <div>
              <Label htmlFor="otp" className="mb-2">
                Verification Code
              </Label>
              <Input
                type="number"
                id="otp"
                name="otp"
                value={formData.otp}
                onChange={handleInputChange}
                className="text-center text-lg tracking-widest"
                placeholder="000000"
                maxLength="6"
                pattern="[0-9]{6}"
                leftIcon={<Mail className="w-5 h-5" />}
                required
              />
              <p className="text-xs mt-2 text-[#125D95FF]">
                Enter the 6-digit code sent to your email
              </p>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resendDisabled || loading}
                  className="text-sm text-[#125D95FF] hover:text-primary-700 disabled:text-text-tertiary disabled:cursor-not-allowed"
                >
                  {resendDisabled
                    ? `Resend OTP in ${resendCountdown}s`
                    : "Resend OTP"}
                </button>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                type="button"
                onClick={goBack}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={loading || formData.otp.length !== 6}
                className="flex-1"
              >
                {loading ? (
                  <InlineLoadSkeleton className="h-4 w-4" />
                ) : (
                  <>
                    {" "}
                    <span>Verify</span> <ArrowRight className="w-4 h-4" />{" "}
                  </>
                )}
              </Button>
            </div>
          </>
        ) : (
          // Step 3: MFA (authenticator app) code
          <>
            <div>
              <Label htmlFor="mfaCode" className="mb-2">
                Authenticator Code
              </Label>
              <Input
                type="text"
                id="mfaCode"
                name="mfaCode"
                value={formData.mfaCode}
                onChange={handleInputChange}
                className="text-center text-lg tracking-widest"
                placeholder="000000"
                maxLength="6"
                pattern="[0-9]{6}"
                leftIcon={<Lock className="w-5 h-5" />}
                required
              />
              <p className="text-xs text-text-tertiary mt-2">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                type="button"
                onClick={goBack}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={loading || formData.mfaCode.length !== 6}
                className="flex-1"
              >
                {loading ? (
                  <InlineLoadSkeleton className="h-4 w-4" />
                ) : (
                  <>
                    {" "}
                    <span>Verify</span> <ArrowRight className="w-4 h-4" />{" "}
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default LoginForm;
